// Public booking tracking page — /track?id=bk_xxx[&email=...]
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { Package, CheckCircle2, Truck, Sparkles, MapPin, Phone, Calendar, ArrowRight, AlertCircle } from "lucide-react";

const STEPS_ICON = [Package, CheckCircle2, Truck, MapPin, Sparkles];

export default function TrackBooking() {
  const { t, lang } = useLang();
  const ct = t.track;
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const idParam = params.get("id") || "";
  const emailParam = params.get("email") || "";

  const [id, setId] = useState(idParam);
  const [email, setEmail] = useState(emailParam);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTracking = async () => {
    if (!id.trim()) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/bookings/track/${id.trim()}`, {
        params: email.trim() ? { email: email.trim() } : {},
      });
      setData(r.data);
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.detail || ct.error_generic);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load if URL contains id
  useEffect(() => {
    if (idParam) {
      fetchTracking();
      // Poll every 30 seconds for live status
      const i = setInterval(fetchTracking, 30000);
      return () => clearInterval(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  const progressLabel = (p) => {
    if (!p) return "";
    return lang === "ar" ? p.label_ar : lang === "en" ? p.label_en : p.label_de;
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-[#0A0B0D] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold tracking-wider uppercase text-neutral-500 hover:text-[#FF5A00] mb-8">
          <ArrowRight size={12} className="rotate-180" /> {ct.back}
        </Link>

        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">{ct.eyebrow}</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-3">{ct.title}</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-8 leading-relaxed">
          {ct.subtitle}
        </p>

        {/* Lookup form */}
        <form
          onSubmit={(e) => { e.preventDefault(); fetchTracking(); }}
          className="bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 p-5 sm:p-6 mb-6 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3"
        >
          <div>
            <label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-500 block mb-1">{ct.booking_id}</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="bk_xxxxxxxxxxxx"
              className="w-full border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF5A00]"
              data-testid="track-input-id"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-500 block mb-1">{ct.email_optional}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF5A00]"
              data-testid="track-input-email"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !id.trim()}
            className="self-end bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wider uppercase text-xs px-5 py-2.5 disabled:opacity-60 transition"
            data-testid="track-submit"
          >
            {loading ? "…" : ct.track_btn}
          </button>
        </form>

        {error && (
          <div className="flex items-start gap-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/40 px-4 py-3 mb-6">
            <AlertCircle size={16} className="text-rose-600 mt-0.5" />
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
          </div>
        )}

        {data && (
          <div className="bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 p-6 sm:p-8" data-testid="track-result">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-6">
              <div>
                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500">{ct.booking_id}</p>
                <p className="font-mono text-base font-bold">{data.booking_id}</p>
              </div>
              <span className="self-start bg-[#090A0B] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5">
                {data.service}
              </span>
            </div>

            {/* Progress steps */}
            <div className="mb-7">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500">{ct.status}</p>
                <span className="text-2xl font-black tracking-tighter text-[#FF5A00] tabular-nums">
                  {data.progress?.percent || 0}%
                </span>
              </div>
              <div className="h-2 bg-neutral-200 dark:bg-neutral-800 mb-4">
                <div
                  className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FF8A3D] transition-all duration-700"
                  style={{ width: `${data.progress?.percent || 0}%` }}
                />
              </div>
              <p className="text-base font-bold">{progressLabel(data.progress)}</p>
            </div>

            <div className="grid grid-cols-5 gap-1 mb-7">
              {STEPS_ICON.map((Icon, i) => {
                const stepNum = i + 1;
                const reached = (data.progress?.step || 0) >= stepNum;
                return (
                  <div
                    key={stepNum}
                    className={`text-center p-2 transition-colors ${reached ? "text-[#FF5A00]" : "text-neutral-300 dark:text-neutral-700"}`}
                  >
                    <Icon size={20} className="mx-auto" />
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-neutral-200 dark:border-neutral-800 pt-5">
              {data.date && <InfoRow icon={Calendar} label={ct.date} value={`${data.date} · ${data.time || ""}`} />}
              {data.address && <InfoRow icon={MapPin} label={ct.address} value={data.address} />}
              {data.phone && <InfoRow icon={Phone} label={ct.phone} value={data.phone} />}
              {data.user_name && <InfoRow icon={Sparkles} label={ct.customer} value={data.user_name} />}
            </div>

            {!data.address && (
              <p className="mt-5 text-[11px] text-neutral-500 bg-neutral-50 dark:bg-neutral-900 p-3 border-s-2 border-[#FF5A00]">
                {ct.email_hint}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-neutral-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-500">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
}
